import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from '../utils/toast';
import { Search, RefreshCw, Eye, ExternalLink } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DemoRequestsPage = () => {
  const [demoRequests, setDemoRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchDemoRequests();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchQuery, demoRequests]);

  const fetchDemoRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/demo-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDemoRequests(response.data);
      setFilteredRequests(response.data);
    } catch (error) {
      toast.error('Failed to fetch demo requests');
      console.error('Error fetching demo requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...demoRequests];
    
    if (searchQuery) {
      filtered = filtered.filter(req =>
        req.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        req.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        req.organization_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        req.phone_number?.includes(searchQuery)
      );
    }
    
    setFilteredRequests(filtered);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Demo Requests</h1>
          <p className="text-gray-600">View and manage demo requests submitted through the website</p>
        </div>

      {/* Filters */}
      <Card className="p-6 mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search by name, email, organization, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button onClick={fetchDemoRequests} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </Card>

      {/* Demo Requests Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Full Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Beds</TableHead>
                <TableHead>Staff</TableHead>
                <TableHead>Submitted On</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                    Loading demo requests...
                  </TableCell>
                </TableRow>
              ) : filteredRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                    No demo requests found
                  </TableCell>
                </TableRow>
              ) : (
                filteredRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell className="font-medium">{request.full_name}</TableCell>
                    <TableCell>
                      <a href={`mailto:${request.email}`} className="text-blue-600 hover:underline">
                        {request.email}
                      </a>
                    </TableCell>
                    <TableCell>
                      <a href={`tel:${request.phone_number}`} className="text-blue-600 hover:underline">
                        {request.phone_number}
                      </a>
                    </TableCell>
                    <TableCell>{request.organization_name}</TableCell>
                    <TableCell>{request.number_of_beds}</TableCell>
                    <TableCell>{request.number_of_staff}</TableCell>
                    <TableCell>{formatDate(request.created_at)}</TableCell>
                    <TableCell>
                      {request.linked_lead_id ? (
                        <Badge className="bg-green-100 text-green-800">
                          Linked to Lead
                        </Badge>
                      ) : (
                        <Badge className="bg-yellow-100 text-yellow-800">
                          Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {request.linked_lead_id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => window.location.href = `/crm/leads/${request.linked_lead_id}`}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          View Lead
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Summary */}
        <div className="border-t border-gray-200 px-6 py-4">
          <p className="text-sm text-gray-600">
            Showing {filteredRequests.length} of {demoRequests.length} demo requests
          </p>
        </div>
      </Card>
      </div>
    </AdminLayout>
  );
};

export default DemoRequestsPage;
